import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateFn, Router } from '@angular/router';
import { filter, map, take } from 'rxjs';
import { AppRoutes } from '../constants/routes.const';
import { AuthService } from '../services/auth.service';
import {
  canAccessApp,
  canAccessOmbudsmanModule,
  canAccessOneOnOneModule,
  canManageTimeTracking,
  canAccessTimeTrackingModule,
  canManageOmbudsmanSettings,
  canManageRecruitment,
  canReviewTimeTracking,
} from '../utils/permissions.utils';

export const authGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const isTimeTrackingRoute = route.data?.['module'] === 'timeTracking';
  const isRecruitmentRoute = route.data?.['module'] === 'recruitment';
  const isOneOnOneRoute = route.data?.['module'] === 'oneOnOne';
  const isOmbudsmanRoute = route.data?.['module'] === 'ombudsman';
  const timeTrackingScope = route.data?.['scope'];
  const ombudsmanScope = route.data?.['scope'];

  return toObservable(authService.isAuthReady).pipe(
    filter(ready => ready),
    take(1),
    map(() => {
      const user = authService.currentUser();
      const claims = authService.userClaims();

      if (!user) {
        const loginUrl = router.parseUrl(AppRoutes.Login);
        const attemptedUrl = state.url;
        if (attemptedUrl && attemptedUrl !== '/' && attemptedUrl !== AppRoutes.Login) {
          loginUrl.queryParams['redirect'] = attemptedUrl;
        }
        return loginUrl;
      }

      if (isTimeTrackingRoute) {
        if (timeTrackingScope === 'manage') {
          return canManageTimeTracking(claims)
            ? true
            : router.parseUrl(AppRoutes.ControleDeHoras.Root);
        }
        if (timeTrackingScope === 'review') {
          return canReviewTimeTracking(claims)
            ? true
            : router.parseUrl(AppRoutes.ControleDeHoras.Root);
        }
        if (canAccessTimeTrackingModule(claims)) {
          return true;
        }
        return router.parseUrl(AppRoutes.Login);
      }

      if (isRecruitmentRoute) {
        if (canManageRecruitment(claims)) {
          return true;
        }

        if (canAccessApp(claims)) {
          return router.parseUrl(AppRoutes.Dashboard.Root);
        }

        if (canAccessTimeTrackingModule(claims)) {
          return router.parseUrl(AppRoutes.ControleDeHoras.Root);
        }

        return router.parseUrl(AppRoutes.Login);
      }

      if (isOneOnOneRoute) {
        return canAccessOneOnOneModule(claims)
          ? true
          : router.parseUrl(AppRoutes.Login);
      }

      if (isOmbudsmanRoute) {
        if (ombudsmanScope === 'settings') {
          return canManageOmbudsmanSettings(claims)
            ? true
            : router.parseUrl(AppRoutes.Ouvidoria.Root);
        }
        if (canAccessOmbudsmanModule(claims)) {
          return true;
        }
        return router.parseUrl(AppRoutes.Login);
      }

      if (canAccessApp(claims)) {
        return true;
      }

      if (canAccessTimeTrackingModule(claims)) {
        return router.parseUrl(AppRoutes.ControleDeHoras.Root);
      }

      return router.parseUrl(AppRoutes.Login);
    })
  );
};
